import type { ReactNode } from "react";
import type { ChipTone } from "@/lib/posting";

/** The card every section of a posting's page sits in. */
export function SectionCard({
    title,
    caption,
    children,
    className = "",
    labelledBy,
}: {
    title?: ReactNode;
    caption?: ReactNode;
    children: ReactNode;
    className?: string;
    labelledBy?: string;
}) {
    return (
        <section
            aria-labelledby={labelledBy}
            className={`flex flex-col rounded-card border border-line bg-card p-5 shadow-card ${className}`}
        >
            {(title || caption) && (
                <div className="flex flex-col gap-0.5">
                    {title && (
                        <h2
                            id={labelledBy}
                            className="m-0 font-display text-card-title font-bold tracking-[-0.01em]"
                        >
                            {title}
                        </h2>
                    )}
                    {caption && (
                        <span className="text-ui text-fg-muted [text-wrap:pretty]">{caption}</span>
                    )}
                </div>
            )}
            {children}
        </section>
    );
}

const PILL: Record<ChipTone, string> = {
    queued: "bg-queued text-queued-fg font-semibold",
    applied: "bg-applied text-applied-fg font-semibold",
    skipped: "bg-skipped text-skipped-fg font-semibold",
    outline: "border border-line-strong text-fg-muted font-semibold",
};

/** Where an application stands, as a pill. */
export function StatusPill({
    label,
    tone,
    size = "md",
    children,
}: {
    label: string;
    tone: ChipTone;
    size?: "sm" | "md";
    children?: ReactNode;
}) {
    const box = size === "sm" ? "px-[9px] py-[3px] text-[11.5px]" : "px-2.5 py-[3px] text-[12px]";
    return (
        <span
            className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full ${box} ${PILL[tone]}`}
        >
            {children}
            {label}
        </span>
    );
}

const LABEL: Record<ChipTone, string> = {
    queued: "text-queued-fg",
    applied: "text-applied-fg",
    skipped: "text-fg-faint",
    outline: "text-fg-faint",
};

/** Where an application stands, as a small word beside the card's actions. */
export function StatusLabel({ label, tone }: { label: string; tone: ChipTone }) {
    return <span className={`text-[12px] leading-4 font-medium ${LABEL[tone]}`}>{label}</span>;
}

/** A quiet fact as a pill: "Posted 10 Oct". */
export function FactPill({ children }: { children: ReactNode }) {
    return (
        <span className="inline-flex items-center rounded-full bg-card-raised px-2.5 py-[3px] text-[12px] font-medium text-fg-muted">
            {children}
        </span>
    );
}

const KEYWORD: Record<"covered" | "missing", string> = {
    covered: "bg-applied text-applied-fg",
    // Outlined, so it reads on the raised wells the Fit sits in.
    missing: "bg-card text-fg-muted ring-1 ring-inset ring-line-strong",
};

/** The drawer's: a fainter green, so the count over them stays the first thing read. */
const QUIET_KEYWORD: Record<"covered" | "missing", string> = {
    ...KEYWORD,
    covered: "bg-brand/10 text-applied-fg",
};

/** One keyword as a chip: on the resume in green, not on it outlined. */
export function KeywordChip({
    term,
    kind,
    className = "px-[9px] py-[3px] text-[12px]",
}: {
    term: string;
    kind: "covered" | "missing";
    className?: string;
}) {
    return (
        <span className={`rounded-full font-semibold ${className} ${KEYWORD[kind]}`}>{term}</span>
    );
}

/** The keywords of one kind as a row of chips: on the resume in green, not on it in grey. */
export function KeywordChips({
    terms,
    kind,
    quiet = false,
}: {
    terms: readonly string[];
    kind: "covered" | "missing";
    /** The drawer's: larger type at a lighter weight, on a fainter green. */
    quiet?: boolean;
}) {
    const chip = quiet
        ? `px-2 py-0.5 text-[14px] leading-5 font-medium ${QUIET_KEYWORD[kind]}`
        : `px-[9px] py-[3px] text-[12px] font-semibold ${KEYWORD[kind]}`;
    return (
        <div className="flex flex-wrap gap-1.5">
            {terms.map((term, index) => (
                <span
                    // A posting can ask for the same word twice.
                    key={`${index}:${term}`}
                    className={`rounded-full ${chip}`}
                >
                    {term}
                </span>
            ))}
        </div>
    );
}

/** The uppercase label over a group. */
export function GroupLabel({ children }: { children: ReactNode }) {
    return (
        <span className="text-small font-semibold uppercase tracking-[0.08em] text-fg-muted">
            {children}
        </span>
    );
}

/**
 * The line over a fit bar, where the count is said once: what is covered a
 * step larger, in the green, then what the words say it is.
 */
export function FitLine({
    covered,
    asked,
    children,
}: {
    covered: number;
    asked: number;
    children: ReactNode;
}) {
    return (
        <span className="text-ui text-fg-muted">
            <span className="font-mono text-read leading-none font-semibold text-brand-ink">
                {covered}
            </span>{" "}
            of <span className="font-mono font-semibold text-fg">{asked}</span> {children}
        </span>
    );
}

/** The fit bar: the gradient over the line, its share filled. */
export function FitBar({ share, height = 10 }: { share: number; height?: number }) {
    return (
        <span
            className="block overflow-hidden rounded-full bg-line"
            style={{ height }}
            aria-hidden="true"
        >
            <span
                className="block h-full rounded-full bg-grad"
                style={{ width: `${Math.round(Math.max(0, Math.min(1, share)) * 100)}%` }}
            />
        </span>
    );
}

/** The PDF tile a resume row leads with. */
export function PdfTile({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
    const box =
        size === "sm"
            ? "h-8 w-[26px] rounded-[5px] text-[8px]"
            : size === "lg"
              ? "h-[50px] w-10 rounded-md text-[9.5px]"
              : "h-10 w-[34px] rounded-md text-[9.5px]";
    return (
        <span
            className={`grid shrink-0 place-items-center border border-line bg-card-raised font-mono font-semibold text-fg-muted ${box}`}
            aria-hidden="true"
        >
            PDF
        </span>
    );
}
